package de.oevbs.kunde.kundenportal2.components

import de.oevbs.kosmos.ui.feedback.alert.alertBox
import de.oevbs.kosmos.ui.foundation.FlowProperty
import de.oevbs.kunde.kundenportal2.authentication.PortalSession
import de.oevbs.sfa.communication.Severity
import dev.fritz2.core.*
import dev.fritz2.headless.foundation.hook
import kotlinx.coroutines.flow.map
import org.w3c.dom.HTMLDivElement
import dev.fritz2.headless.validation.Severity as FritzSeverity

/**
 * Rendert ein Layout für eine Seite, das aus einer Überschrift, einem Content-Bereich sowie optional einem
 * Header- oder Hintergrundbild besteht.
 *
 * Siehe [headerImageLayout] für die vollständige Dokumentation.
 */
class HeaderImageLayout {

    companion object {
        private const val headerImageHeightClasses = "h-48 lg:h-72"
        private const val titleBaseClasses = "z-10 text-3xl font-semibold"
    }


    /**
     * Titel, der über dem [content] angezeigt werden soll.
     */
    val title: FlowProperty<String> = FlowProperty()

    /**
     * Der eigentliche Seiteninhalt.
     */
    val content: ContentHook = ContentHook()

    /**
     * Optionaler, relativer Pfad zu einem zu verwendenden Hintergrundbild.
     * Der Pfad ist relativ zur Wurzel des `resources`-Ordners.
     *
     * Auch wenn Background- und Header simultan verwendet werden können, sollte aus optischen Gründen in der Regel
     * nur eine der beiden Properties gesetzt werden.
     *
     * Beispiel:
     * ```
     * backgroundImage = "images/backgrounds/willkommen.jpg"
     * ```
     *
     * @see headerImage
     */
    var backgroundImage: String? = null

    /**
     * Optionaler, relativer Pfad zu einem zu verwendenden Header-Bild.
     * Der Pfad ist relativ zur Wurzel des `resources`-Ordners.
     *
     * Auch wenn Background- und Header simultan verwendet werden können, sollte aus optischen Gründen in der Regel
     * nur eine der beiden Properties gesetzt werden.
     *
     * Beispiel:
     * ```
     * headerImage = "images/headers/kontakt.jpg"
     * ```
     *
     * @see backgroundImage
     */
    var headerImage: String? = null

    /**
     * Wenn dieser Wert true ist, wird die Überschrift auf dem HeaderImage in weiß statt in primär-blau angezeigt.
     *
     * @see backgroundImage
     */
    var headerImageIsDark: Boolean? = null


    private fun Tag<HTMLDivElement>.backgroundImageStyle(path: String) {
        inlineStyle("""
            background-image: url($path);
            background-size: cover;
            background-position: center center;
        """.trimIndent())
    }


    fun RenderContext.render() {

        div("relative w-full h-full px-4 pb-4 lg:px-12 xl:px-36 2xl:px-52 flex justify-center") {
            backgroundImage?.let { bg ->
                backgroundImageStyle(bg)
            }

            div("w-full max-w-screen-2xl flex flex-col gap-4 lg:gap-8") {
                when (val image = headerImage) {
                    null -> renderTitleWithoutHeader()
                    else -> renderTitleWithHeader(image)
                }

                // Leeren des globalen Error-Stores basierend auf dem Login-Status des Nutzers beim Laden einer Seite.
                //
                // Wenn abgemeldet: Leere nur Messages, die nicht Sticky sind. So können sticky Meldungen auch nach dem
                // Löschen oder Logout eines Nutzers bestehen bleiben.
                //
                // Wenn angemeldet: Leere alle Messages, auch solche, die als Sticky markiert sind.
                if (PortalSession.current == null) GlobalServiceMessagesStore.clear()
                else GlobalServiceMessagesStore.forceClear()

                renderGlobalServiceMessages()

                if (content.isSet) {
                    hook(content)
                }
            }
        }
    }

    private fun RenderContext.renderTitleWithoutHeader() {
        h2(
            joinClasses(
                titleBaseClasses,
                "pt-4 lg:pt-12 text-primary-700"
            )
        ) {
            title.value?.renderText(into = this)
        }
    }

    private fun RenderContext.renderTitleWithHeader(headerImagePath: String) {
        h2(
            joinClasses(
                titleBaseClasses,
                "flex flex-col justify-center",
                if (headerImageIsDark == true)  "text-primary-300" else "text-primary-700",
                headerImageHeightClasses
            )
        ) {
            title.value?.renderText(into = this)
        }
        div(joinClasses("absolute top-0 left-0 w-full", headerImageHeightClasses)) {
            backgroundImageStyle(headerImagePath)
        }
    }

    private fun RenderContext.renderGlobalServiceMessages() {
        ul("sticky top-4 z-10 space-y-4") {
            className(GlobalServiceMessagesStore.data.map {
                if (it.isEmpty()) "hidden" else "visible"
            }, initial = "hidden")

            GlobalServiceMessagesStore.data.map { entries ->
                entries.map(GlobalServiceMessagesStore.Entry::message)
            }.renderEach(into = this) { message ->
                li("shadow-md rounded-lg") {
                    alertBox {
                        title(message.message)
                        message.details?.let { description(it) }
                        severity = when(message.severity) {
                            Severity.Success -> FritzSeverity.Success
                            Severity.Info -> FritzSeverity.Info
                            Severity.Warning -> FritzSeverity.Warning
                            Severity.Error -> FritzSeverity.Error
                            Severity.Unexpected -> FritzSeverity.Error
                        }
                    }
                }
            }
        }
    }
}


/**
 * Rendert ein Layout für eine Seite, das aus einer Überschrift, einem Content-Bereich sowie optional einem
 * Header- oder Hintergrundbild besteht.
 *
 * Das Layout nutzt den vollen verfügbaren Platz und zentriert den Content horizontal, falls die Seite eine gewisse
 * Größe übersteigt.
 *
 * Beispiel:
 * ```
 * headerImageLayout {
 *     title("Hallo, Welt!")
 *     content("Lorem ipsum, dolor sit amet...")
 *
 *     // In der Regel nur eines von beidem:
 *     headerImage = "images/headers/kontakt.jpg"
 *     backgroundImage = "images/backgrounds/willkommen.jpg"
 * }
 * ```
 *
 * @see HeaderImageLayout.title
 * @see HeaderImageLayout.content
 * @see HeaderImageLayout.headerImage
 * @see HeaderImageLayout.backgroundImage
 */
fun RenderContext.headerImageLayout(
    initialize: HeaderImageLayout.() -> Unit = {}
): Unit =
    HeaderImageLayout().apply(initialize).run { render() }